import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle,
  Calendar,
  Camera,
  Eye,
  CheckCircle,
  Building,
  User,
  Clock,
  Star,
  X,
  FileText,
  Target,
  Gift,
  Users,
  ArrowRight,
  Check,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand Colors
const COLORS = {
  primary: '#2196F3',      // Blu
  primaryDark: '#0077CC',  // Blu scuro
  secondary: '#FF9800',    // Arancione
  secondaryLight: '#FFB74D'
};

const PublicQuoteViewer = () => {
  const { urlSlug } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseType, setResponseType] = useState('');
  const [responseNotes, setResponseNotes] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    fetchPublicQuote();
  }, [urlSlug]);

  useEffect(() => {
    if (quote?.gallery?.images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === quote.gallery.images.length - 1 ? 0 : prev + 1
        );
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [quote]);

  const fetchPublicQuote = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/quotes/${urlSlug}`);
      setQuote(response.data);
      if (response.data?.client_registry) {
        setClientName(`${response.data.client_registry.first_name} ${response.data.client_registry.last_name}`);
        setClientEmail(response.data.client_registry.email || '');
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError(err.response?.data?.detail || 'Preventivo non trovato');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteResponse = async () => {
    try {
      if (!responseType) {
        toast.error('Seleziona una risposta');
        return;
      }

      const responseData = {
        response: responseType,
        notes: responseNotes,
        client_email: clientEmail,
        client_name: clientName
      };

      await axios.post(`${API}/public/quotes/${urlSlug}/respond`, responseData);
      
      // Prepare WhatsApp message
      const clientFullName = clientName || `${quote.client_registry?.first_name || ''} ${quote.client_registry?.last_name || ''}`.trim() || 'Cliente';
      const statusText = responseType === 'accepted' ? '✅ ACCETTATO' : '❌ RIFIUTATO';
      const whatsappMessage = `📋 *Preventivo ${statusText}*\n\n` +
        `📌 *Titolo:* ${quote.title}\n` +
        `👤 *Cliente:* ${clientFullName}\n` +
        `📅 *Data:* ${new Date().toLocaleDateString('it-IT')}\n` +
        (responseNotes ? `📝 *Note:* ${responseNotes}\n` : '') +
        `\n_Messaggio inviato automaticamente da Tribù in Viaggio_`;
      
      const agentWhatsApp = quote.contacts?.agent_whatsapp;
      
      if (agentWhatsApp) {
        const whatsappNumber = agentWhatsApp.replace(/\D/g, '');
        const encodedMessage = encodeURIComponent(whatsappMessage);
        window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
        toast.success(`Preventivo ${responseType === 'accepted' ? 'accettato' : 'rifiutato'}! Si aprirà WhatsApp per notificare l'agente.`);
      } else {
        toast.success(`Preventivo ${responseType === 'accepted' ? 'accettato' : 'rifiutato'}! L'agente riceverà una notifica.`);
      }
      
      setShowResponseDialog(false);
      fetchPublicQuote();
      
    } catch (error) {
      console.error('Error responding to quote:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della risposta');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const nextImage = () => {
    if (quote?.gallery?.images?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === quote.gallery.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (quote?.gallery?.images?.length > 0) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? quote.gallery.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${COLORS.primary}15 0%, ${COLORS.secondary}15 100%)` }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: COLORS.primary }}></div>
          <p style={{ color: COLORS.primaryDark }}>Caricamento preventivo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${COLORS.primary}15 0%, ${COLORS.secondary}15 100%)` }}>
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Preventivo non trovato</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!quote) return null;

  const isExpired = quote.is_expired;
  const hasResponded = quote.client_response !== null;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${COLORS.primary}08 0%, ${COLORS.secondary}08 100%)` }}>
      {/* Header with Brand Colors */}
      <header className="text-white py-6 shadow-lg" style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            {/* Logo */}
            {quote.agent_logo_url && (
              <div className="flex-shrink-0">
                <img 
                  src={quote.agent_logo_url.startsWith('http') ? quote.agent_logo_url : `${BACKEND_URL}${quote.agent_logo_url}`}
                  alt="Logo Agenzia"
                  className="h-16 w-16 object-contain bg-white rounded-lg p-1"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold">{quote.title}</h1>
                {quote.destination?.flag_emoji && (
                  <span className="text-3xl">{quote.destination.flag_emoji}</span>
                )}
                {quote.travel_type && (
                  <Badge className="text-white border-white/30" style={{ backgroundColor: COLORS.secondary }}>
                    {quote.travel_type}
                  </Badge>
                )}
              </div>
              <p className="text-lg text-white/90 mb-2">
                per {quote.client_registry ? 
                  `${quote.client_registry.first_name} ${quote.client_registry.last_name}` : 
                  'Cliente'
                }
              </p>
              <div className="flex items-center gap-4 text-sm text-white/80 flex-wrap">
                <div className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {quote.contacts?.agency_name || 'Tribù in Viaggio'}
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {quote.views_count} visualizzazioni
                </div>
                {quote.validity_date && (
                  <div className={`flex items-center gap-1 ${isExpired ? 'text-red-300' : 'text-green-300'}`}>
                    <Clock className="w-4 h-4" />
                    {isExpired ? 
                      `Scaduto il ${new Date(quote.validity_date).toLocaleDateString('it-IT')}` :
                      `Valido fino al ${new Date(quote.validity_date).toLocaleDateString('it-IT')}`
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Status Badges */}
      {(isExpired || hasResponded) && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
          {isExpired && (
            <Badge variant="destructive" className="text-lg py-2 px-4">
              ⚠️ Preventivo Scaduto
            </Badge>
          )}
          {hasResponded && !isExpired && (
            <Badge className={`text-lg py-2 px-4 ${quote.client_response === 'accepted' ? 'bg-green-500' : 'bg-red-500'}`}>
              {quote.client_response === 'accepted' ? '✅ Preventivo Accettato' : '❌ Preventivo Rifiutato'}
            </Badge>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          
          {/* Gallery Fotografica */}
          {quote.gallery?.images?.length > 0 && (
            <Card className="overflow-hidden border-0 shadow-lg">
              <div className="relative">
                <div className="aspect-video bg-slate-100">
                  <img
                    src={quote.gallery.images[currentImageIndex].startsWith('http') ? 
                      quote.gallery.images[currentImageIndex] : 
                      `${BACKEND_URL}${quote.gallery.images[currentImageIndex]}`
                    }
                    alt={`Immagine ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/800x400/e2e8f0/64748b?text=${encodeURIComponent(quote.destination?.city || 'Destinazione')}`;
                    }}
                  />
                </div>
                
                {quote.gallery.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                    >
                      ←
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
                    >
                      →
                    </button>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                      {quote.gallery.images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentImageIndex(idx)}
                          className={`w-3 h-3 rounded-full transition-all ${
                            idx === currentImageIndex ? 'scale-125' : 'bg-white/60'
                          }`}
                          style={{ backgroundColor: idx === currentImageIndex ? COLORS.secondary : undefined }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Card>
          )}

          {/* Introduzione */}
          {quote.introduction && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.primary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <FileText className="w-5 h-5" />
                  Benvenuto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.introduction}</p>
              </CardContent>
            </Card>
          )}

          {/* Contesto del Viaggio */}
          {quote.travel_context && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.secondary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <MapPin className="w-5 h-5" />
                  Il Contesto del Viaggio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.travel_context}</p>
              </CardContent>
            </Card>
          )}

          {/* Dettagli del Viaggio - Date e Partecipanti */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1" style={{ backgroundColor: COLORS.primary }}></div>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                <Calendar className="w-5 h-5" />
                Dettagli del Viaggio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10` }}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                    📅 Periodo
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Partenza:</span>
                      <span className="font-medium">
                        {quote.start_date ? new Date(quote.start_date).toLocaleDateString('it-IT', { 
                          weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' 
                        }) : 'Da definire'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rientro:</span>
                      <span className="font-medium">
                        {quote.end_date ? new Date(quote.end_date).toLocaleDateString('it-IT', { 
                          weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' 
                        }) : 'Da definire'}
                      </span>
                    </div>
                    {quote.nights > 0 && (
                      <div className="flex justify-between pt-2 border-t" style={{ borderColor: `${COLORS.primary}30` }}>
                        <span className="text-slate-600">Durata:</span>
                        <span className="font-bold" style={{ color: COLORS.primary }}>
                          🌙 {quote.nights} nott{quote.nights === 1 ? 'e' : 'i'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Partecipanti */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: `${COLORS.secondary}10` }}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                    👥 Partecipanti
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Adulti:</span>
                      <span className="font-medium">{quote.client_registry?.participants?.adults || 2}</span>
                    </div>
                    {(quote.client_registry?.participants?.children || 0) > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Bambini:</span>
                          <span className="font-medium">{quote.client_registry.participants.children}</span>
                        </div>
                        {quote.client_registry.participants.children_ages?.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Età:</span>
                            <span className="font-medium">{quote.client_registry.participants.children_ages.join(', ')} anni</span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex justify-between pt-2 border-t" style={{ borderColor: `${COLORS.secondary}30` }}>
                      <span className="text-slate-600">Totale:</span>
                      <span className="font-bold" style={{ color: COLORS.secondary }}>
                        {(quote.client_registry?.participants?.adults || 2) + (quote.client_registry?.participants?.children || 0)} persone
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spiegazione della Scelta */}
          {quote.choice_explanation && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.secondary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <Target className="w-5 h-5" />
                  Perché Questa Scelta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.choice_explanation}</p>
              </CardContent>
            </Card>
          )}

          {/* Benefici per il Cliente */}
          {quote.client_benefits && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.primary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <Gift className="w-5 h-5" />
                  I Vostri Benefici
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.client_benefits}</p>
              </CardContent>
            </Card>
          )}

          {/* Per Chi è Ideale */}
          {quote.ideal_for && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.secondary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <Users className="w-5 h-5" />
                  Per Chi è Ideale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{quote.ideal_for}</p>
              </CardContent>
            </Card>
          )}

          {/* Itinerario (se presente) */}
          {quote.itinerary?.days?.length > 0 && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.primary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <Calendar className="w-5 h-5" />
                  Itinerario di Viaggio
                </CardTitle>
                <CardDescription>
                  Programma dettagliato del tuo {quote.travel_type}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {quote.itinerary.days.map((day, index) => (
                    <div key={index} className="p-4 border rounded-lg" style={{ borderColor: `${COLORS.primary}30`, background: `linear-gradient(135deg, ${COLORS.primary}05 0%, ${COLORS.secondary}05 100%)` }}>
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 text-white rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: COLORS.primary }}>
                            {day.day_number}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold mb-1" style={{ color: COLORS.primaryDark }}>{day.location}</h3>
                          <div className="space-y-2 text-sm">
                            {day.activities && (
                              <div>
                                <span className="font-medium text-slate-700">Programma: </span>
                                <span className="text-slate-600">{day.activities}</span>
                              </div>
                            )}
                            {day.meals && (
                              <div>
                                <span className="font-medium text-slate-700">Pasti: </span>
                                <span className="text-slate-600">{day.meals}</span>
                              </div>
                            )}
                            {day.accommodation && (
                              <div>
                                <span className="font-medium text-slate-700">Pernottamento: </span>
                                <span className="text-slate-600">{day.accommodation}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Destinazione Info */}
          {quote.destination && (quote.destination.climate || quote.destination.best_period || quote.destination.attractions || quote.destination.typical_food) && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{ backgroundColor: COLORS.secondary }}></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                  <MapPin className="w-5 h-5" />
                  {quote.destination.city}, {quote.destination.country}
                  {quote.destination.flag_emoji && <span className="text-2xl">{quote.destination.flag_emoji}</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quote.destination.climate && (
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-1">🌡️ Clima</p>
                      <p className="text-slate-600">{quote.destination.climate}</p>
                    </div>
                  )}
                  {quote.destination.best_period && (
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-1">📅 Periodo Ideale</p>
                      <p className="text-slate-600">{quote.destination.best_period}</p>
                    </div>
                  )}
                  {quote.destination.attractions && (
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-1">⭐ Attrazioni</p>
                      <p className="text-slate-600">{quote.destination.attractions}</p>
                    </div>
                  )}
                  {quote.destination.typical_food && (
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-1">🍽️ Cucina Tipica</p>
                      <p className="text-slate-600">{quote.destination.typical_food}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* IL PROSSIMO PASSO - Prezzi e Azioni */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)` }}></div>
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl flex items-center justify-center gap-2" style={{ color: COLORS.primaryDark }}>
                <ArrowRight className="w-6 h-6" />
                Il Prossimo Passo
              </CardTitle>
              <CardDescription>
                Riepilogo economico e conferma del preventivo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Servizi Inclusi */}
              {quote.services?.length > 0 && (
                <div className="space-y-4">
                  {/* Pacchetto Base */}
                  {quote.services.filter(s => s.is_included).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.primary }}>
                        <CheckCircle className="w-5 h-5" />
                        Pacchetto Base Incluso
                      </h4>
                      <div className="space-y-2">
                        {quote.services.filter(s => s.is_included).map((service, idx) => (
                          <div key={idx} className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10` }}>
                            <div>
                              <span className="font-medium">{service.name}</span>
                              {service.description && (
                                <p className="text-sm text-slate-600">{service.description}</p>
                              )}
                            </div>
                            <span className="font-semibold" style={{ color: COLORS.primary }}>{formatCurrency(service.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Supplementi Opzionali */}
                  {quote.services.filter(s => !s.is_included).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.secondary }}>
                        <Star className="w-5 h-5" />
                        Supplementi Opzionali
                      </h4>
                      <div className="space-y-2">
                        {quote.services.filter(s => !s.is_included).map((service, idx) => (
                          <div key={idx} className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: `${COLORS.secondary}10` }}>
                            <div>
                              <span className="font-medium">{service.name}</span>
                              {service.description && (
                                <p className="text-sm text-slate-600">{service.description}</p>
                              )}
                            </div>
                            <span className="font-semibold" style={{ color: COLORS.secondary }}>{formatCurrency(service.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Riepilogo Prezzi */}
              <div className="p-4 rounded-lg bg-slate-50 border">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Pacchetto Base:</span>
                    <span className="font-medium">{formatCurrency(quote.total_base_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Supplementi:</span>
                    <span className="font-medium">{formatCurrency(quote.total_supplements_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotale:</span>
                    <span className="font-medium">{formatCurrency(quote.total_base_price + quote.total_supplements_price)}</span>
                  </div>
                  
                  {quote.discount_amount > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Sconto applicato:</span>
                        <span className="font-medium">-{formatCurrency(quote.discount_amount)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-400 line-through">
                        <span>Prezzo originale:</span>
                        <span>{formatCurrency(quote.total_base_price + quote.total_supplements_price)}</span>
                      </div>
                    </>
                  )}
                  
                  <hr className="my-3" />
                  
                  <div className="flex justify-between text-xl font-bold">
                    <span>TOTALE FINALE:</span>
                    <span style={{ color: COLORS.primary }}>{formatCurrency(quote.final_price)}</span>
                  </div>
                </div>
              </div>

              {/* Pulsanti Accetta/Rifiuta */}
              {!isExpired && !hasResponded && (
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    className="flex-1 py-6 text-lg font-semibold text-white hover:opacity-90"
                    style={{ backgroundColor: '#22c55e' }}
                    onClick={() => {
                      setResponseType('accepted');
                      setShowResponseDialog(true);
                    }}
                  >
                    <Check className="w-5 h-5 mr-2" />
                    Accetto il Preventivo
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 py-6 text-lg font-semibold border-2 hover:bg-red-50"
                    style={{ borderColor: '#ef4444', color: '#ef4444' }}
                    onClick={() => {
                      setResponseType('rejected');
                      setShowResponseDialog(true);
                    }}
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    Rifiuto il Preventivo
                  </Button>
                </div>
              )}

              {hasResponded && (
                <div className={`p-4 rounded-lg text-center ${quote.client_response === 'accepted' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <p className="font-semibold text-lg">
                    {quote.client_response === 'accepted' ? 
                      '✅ Hai accettato questo preventivo' : 
                      '❌ Hai rifiutato questo preventivo'}
                  </p>
                  {quote.client_response_date && (
                    <p className="text-sm mt-1">
                      in data {new Date(quote.client_response_date).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              )}

              {isExpired && !hasResponded && (
                <div className="p-4 rounded-lg bg-amber-50 text-amber-800 text-center">
                  <p className="font-semibold">⚠️ Questo preventivo è scaduto</p>
                  <p className="text-sm mt-1">Contatta l'agenzia per un nuovo preventivo</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contatti Agente */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="h-1" style={{ backgroundColor: COLORS.primary }}></div>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: COLORS.primaryDark }}>
                <User className="w-5 h-5" />
                Il Tuo Consulente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="flex-1 text-center sm:text-left">
                  <p className="font-semibold text-lg">{quote.contacts?.agent_name}</p>
                  <p className="text-slate-600">{quote.contacts?.agency_name}</p>
                </div>
                <div className="flex gap-3">
                  {quote.contacts?.agent_email && (
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = `mailto:${quote.contacts.agent_email}?subject=Preventivo: ${quote.title}`}
                      style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                  )}
                  {quote.contacts?.agent_whatsapp && (
                    <Button
                      className="text-white"
                      style={{ backgroundColor: '#25D366' }}
                      onClick={() => {
                        const msg = encodeURIComponent(`Ciao! Ho una domanda sul preventivo "${quote.title}"`);
                        const num = quote.contacts.agent_whatsapp.replace(/\D/g, '');
                        window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
                      }}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 mt-8 text-center text-white" style={{ backgroundColor: COLORS.primaryDark }}>
        <p className="text-sm">
          © {new Date().getFullYear()} {quote.contacts?.agency_name || 'Tribù in Viaggio'} - Tutti i diritti riservati
        </p>
      </footer>

      {/* Response Dialog */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {responseType === 'accepted' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Conferma Accettazione
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-500" />
                  Conferma Rifiuto
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {responseType === 'accepted' 
                ? 'Stai per accettare questo preventivo. L\'agente verrà notificato tramite WhatsApp.'
                : 'Stai per rifiutare questo preventivo. Puoi aggiungere una motivazione.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Il tuo nome</Label>
              <Input 
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nome e Cognome"
              />
            </div>
            
            <div className="space-y-2">
              <Label>La tua email</Label>
              <Input 
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="email@esempio.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note aggiuntive (opzionale)</Label>
              <Textarea
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder={responseType === 'accepted' 
                  ? 'Es: Confermo tutto, procediamo!'
                  : 'Es: Il budget è troppo alto, vorrei valutare alternative...'}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleQuoteResponse}
              className={responseType === 'accepted' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {responseType === 'accepted' ? 'Conferma Accettazione' : 'Conferma Rifiuto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicQuoteViewer;
